/**
 * V2 admin — Personal categories tab (docs/JOURNAL-PLAN.md §11).
 *
 * Every user-owned shelf, searchable and sortable, 25 per page —
 * modeled on the Lists tab, because a personal category is the same
 * shape of thing: user-owned, named, shareable, and NOT admin-curated.
 * (Site categories keep their own tab; mixing the two would blur the
 * one distinction §11 exists to draw.)
 *
 * Why this tab exists at all: since 20260811d these names are PUBLIC —
 * they ride profile tiles, yoink pages, share cards and the shelf URL —
 * and the delete policy from 20260811c has always said "admin moderates
 * names" with no surface to do it from. Delete is the moderation tool
 * and it is non-destructive to the yoinks: the FK is ON DELETE SET
 * NULL, so they land in the owner's Uncategorized tile, never deleted.
 * Renaming stays owner-only by policy (an admin recategorizes yoinks;
 * they don't rewrite someone's words).
 */
import * as gateway from '../../core/gateway.js';
import { invalidate } from '../../core/query.js';
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { confirmDialog } from './confirm.js';
import { fetchAll } from './data.js';

const PER_PAGE = 25;

/** Search + sort (same shape as filterLists). Exported for tests. */
export function filterPersonal(rows, { search = '', sortColumn = 'created_at', sortDirection = 'desc' } = {}) {
  let result = [...rows];
  if (search) {
    const term = search.toLowerCase();
    result = result.filter((c) =>
      (c.name || '').toLowerCase().includes(term) ||
      c.ownerName.toLowerCase().includes(term) ||
      (c.description || '').toLowerCase().includes(term));
  }
  result.sort((a, b) => {
    const pick = (c) => {
      switch (sortColumn) {
        case 'name': return (c.name || '').toLowerCase();
        case 'owner': return c.ownerName.toLowerCase();
        case 'yoinks': return c.yoink_count;
        default: return new Date(c.created_at).getTime();
      }
    };
    const aVal = pick(a);
    const bVal = pick(b);
    if (sortColumn === 'created_at' || sortColumn === 'yoinks') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  return result;
}

export function mount(el, ctx) {
  const { router } = ctx;
  let destroyed = false;
  let all = [];
  let loaded = false;
  let loadError = '';
  let search = '';
  let sortColumn = 'created_at';
  let sortDirection = 'desc';
  let page = 1;
  let storyUnavailable = false; // 20260811e not applied → column hidden

  async function load() {
    const base = 'id,owner_id,name,emoji,slug,created_at';
    const [shelves, users, yoinks] = await Promise.all([
      // description predates its migration in some environments — the
      // lists-tab idiom: fall back to the plain shape rather than 400
      fetchAll('personal_categories', { select: `${base},description`, order: 'created_at.desc' })
        .catch(() => {
          storyUnavailable = true;
          return fetchAll('personal_categories', { select: base, order: 'created_at.desc' });
        }),
      fetchAll('profiles', { select: 'id,full_name,username', order: 'full_name.asc' }),
      // Counts come from the yoinks themselves — no embed: an FK embed
      // of a column the database may not have 400s the whole query
      fetchAll('yoinks', {
        select: 'personal_category_id',
        filters: { personal_category_id: 'not.is.null' },
      }).catch(() => []),
    ]);
    const byId = new Map(users.map((u) => [u.id, { ...u, displayName: u.full_name || u.username || '(No name)' }]));
    const counts = new Map();
    for (const y of yoinks) {
      counts.set(y.personal_category_id, (counts.get(y.personal_category_id) || 0) + 1);
    }
    all = shelves.map((c) => ({
      ...c,
      yoink_count: counts.get(c.id) || 0,
      ownerName: byId.get(c.owner_id)?.displayName || 'Unknown',
      ownerUsername: byId.get(c.owner_id)?.username || null,
    }));
    loaded = true;
  }

  const arrow = (col) => (sortColumn === col ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '');

  function shown() {
    return filterPersonal(all, { search, sortColumn, sortDirection });
  }

  function render() {
    if (loadError) {
      el.innerHTML = `<div class="v2-error">${escapeHtml(loadError)}</div>`;
      return;
    }
    if (!loaded) {
      el.innerHTML = '<div class="v2-loading">Unrolling the shelves…</div>';
      return;
    }
    const list = shown();
    const totalPages = Math.ceil(list.length / PER_PAGE) || 1;
    if (page > totalPages) page = totalPages;
    const pageRows = list.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    const cols = storyUnavailable ? 5 : 6;
    el.innerHTML = `
      <div class="admin-card admin-card-span">
        <div class="admin-toolbar">
          <input type="search" class="admin-search" data-personal-search aria-label="Search personal categories"
            placeholder="Search personal categories…" value="${escapeHtml(search)}" autocomplete="off">
          <span class="admin-muted">${list.length} of ${all.length}</span>
          <span class="admin-toolbar-spacer"></span>
          <button type="button" class="admin-btn" data-page-prev ${page <= 1 ? 'disabled' : ''}>←</button>
          <span class="admin-muted">Page ${page} of ${totalPages}</span>
          <button type="button" class="admin-btn" data-page-next ${page >= totalPages ? 'disabled' : ''}>→</button>
        </div>
        <p class="admin-muted">These are explorers’ own shelves — their yoinks stay off the public map, feeds and
          category counts, but the names are public. Deleting one leaves its yoinks in the owner’s journal,
          uncategorized. Renaming is the owner’s to do.</p>
        <div class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                ${[['name', 'Category'], ['owner', 'Owner'], ['yoinks', 'Yoinks'], ['created_at', 'Created']]
                  .map(([col, label]) => `<th class="admin-sortable" data-sort-personal="${col}">${label}${arrow(col)}</th>`).join('')}
                ${storyUnavailable ? '' : '<th title="Has the owner written a story?">Story</th>'}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${pageRows.length ? pageRows.map((c) => `
                <tr>
                  <td class="admin-cell-clamp">${c.emoji ? escapeHtml(c.emoji) : '🔒'} ${escapeHtml(c.name)}</td>
                  <td>${c.ownerUsername
                    ? `<button type="button" class="admin-link" data-personal-owner="${escapeHtml(c.ownerUsername)}">${escapeHtml(c.ownerName)}</button>`
                    : `<span class="admin-cell-dim">${escapeHtml(c.ownerName)}</span>`}</td>
                  <td>${c.yoink_count}</td>
                  <td class="admin-cell-dim">${new Date(c.created_at).toLocaleDateString(undefined, { timeZone: 'America/Los_Angeles' })}</td>
                  ${storyUnavailable ? '' : `<td class="admin-cell-clamp admin-cell-dim">${
                    c.description ? escapeHtml(c.description) : '—'}</td>`}
                  <td>
                    <div class="admin-actions">
                      <button type="button" class="admin-btn admin-btn-xs" data-view-personal="${c.id}"
                        ${c.ownerUsername ? '' : 'disabled title="Owner has no username — no shelf URL"'}>View</button>
                      <button type="button" class="admin-btn admin-btn-xs admin-btn-danger"
                        data-delete-personal="${c.id}">Delete</button>
                    </div>
                  </td>
                </tr>`).join('') : `<tr><td colspan="${cols}" class="admin-empty-cell">No personal categories found</td></tr>`}
            </tbody>
          </table>
        </div>
      </div>`;

    el.querySelector('[data-personal-search]')?.addEventListener('input', debounce((e) => {
      const caret = e.target.selectionStart;
      search = e.target.value;
      page = 1;
      render();
      const again = el.querySelector('[data-personal-search]');
      if (again) { again.focus(); again.setSelectionRange(caret, caret); }
    }, 300));
  }

  async function deletePersonal(id) {
    const shelf = all.find((c) => c.id === id);
    if (!shelf) return;
    const n = shelf.yoink_count;
    if (!(await confirmDialog({
      title: `Delete "${shelf.name}"?`,
      message: n
        ? `${escapeHtml(shelf.ownerName)}’s ${n} yoink${n === 1 ? '' : 's'} stay${n === 1 ? 's' : ''} in their journal — `
          + 'they just become uncategorized. The shelf and its story are gone for good.'
        : 'The shelf is empty — nothing else changes.',
    }))) return;
    const { error } = await gateway.rawDelete(gateway.TABLES.PERSONAL_CATEGORIES, {
      filters: { id: `eq.${id}` },
    });
    if (destroyed) return;
    if (error) return showToast('Couldn’t delete the category.', { type: 'info' });
    all = all.filter((c) => c.id !== id);
    invalidate('personal:'); // its page and the owner's tiles just changed
    invalidate('profile-all-yoinks:'); // their shelf pointers went null
    showToast('Personal category deleted — its yoinks are now uncategorized.', { type: 'success' });
    render();
  }

  function onClick(event) {
    const t = event.target;
    const sortTh = t.closest('[data-sort-personal]');
    if (sortTh) {
      const col = sortTh.dataset.sortPersonal;
      if (sortColumn === col) sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      else { sortColumn = col; sortDirection = col === 'name' || col === 'owner' ? 'asc' : 'desc'; }
      page = 1;
      return render();
    }
    if (t.closest('[data-page-prev]')) { if (page > 1) { page--; render(); } return; }
    if (t.closest('[data-page-next]')) { page++; return render(); }
    const owner = t.closest('[data-personal-owner]');
    if (owner) return router.navigate('user', { username: owner.dataset.personalOwner });
    const view = t.closest('[data-view-personal]');
    if (view) {
      const shelf = all.find((c) => c.id === view.dataset.viewPersonal);
      if (shelf?.ownerUsername) {
        router.navigate('personal-category', { username: shelf.ownerUsername, slug: shelf.slug });
      }
      return;
    }
    const del = t.closest('[data-delete-personal]');
    if (del) return deletePersonal(del.dataset.deletePersonal);
  }

  render();
  load().then(() => { if (!destroyed) render(); })
    .catch(() => {
      if (destroyed) return;
      // The likeliest cause is 20260811c/d not being applied — say so
      loadError = 'Couldn’t load the personal categories. If the §11 migrations haven’t run yet, that’s why.';
      render();
    });

  el.addEventListener('click', onClick);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
  };
}
